import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { scrollToId } from "@/lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden">
      <motion.img
        src="https://images.unsplash.com/photo-1724582586458-a51791349977?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwaW50ZXJpb3IlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzg3MjkyNTIxfDA&ixlib=rb-4.1.0&q=85&w=2000&auto=format&fit=crop"
        alt="Premium modern minimalist living room interior designed in Mumbai"
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0 w-full h-full object-cover will-change-transform"
      />
      <div className="absolute inset-0 bg-black/45" />

      <motion.div style={{ opacity: contentOpacity }} className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pb-28 pt-48 w-full">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.1 }}
          className="text-xs tracking-[0.25em] uppercase font-semibold text-zinc-300 mb-6"
        >
          Civil &bull; Interior &bull; Modular Kitchen &bull; Restoration
        </motion.p>
        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight text-white max-w-4xl leading-[1.05]">
          <MaskedLine delay={0.25}>Crafting Spaces.</MaskedLine>
          <MaskedLine delay={0.45}>Building Quality.</MaskedLine>
        </h1>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
        >
          <p className="mt-6 text-lg lg:text-xl text-zinc-200 font-medium max-w-2xl">
            Professional Civil, Interior &amp; Modular Kitchen Solutions in Mumbai.
          </p>
          <p className="mt-4 text-base leading-relaxed text-zinc-300 max-w-xl">
            From civil execution to refined interiors, we create functional and beautifully
            finished spaces with attention to quality, detail and workmanship.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <button
              data-testid="hero-quote-btn"
              onClick={() => scrollToId("contact")}
              className="bg-white text-zinc-950 text-sm font-semibold px-8 py-4 rounded-sm hover:bg-zinc-200 transition-colors duration-300"
            >
              Get a Free Quote
            </button>
            <button
              data-testid="hero-work-btn"
              onClick={() => scrollToId("projects")}
              className="border border-white/60 text-white text-sm font-semibold px-8 py-4 rounded-sm hover:bg-white hover:text-zinc-950 transition-colors duration-300"
            >
              View Our Work
            </button>
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="hidden sm:flex absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex-col items-center gap-3"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-400">Scroll</span>
        <motion.span
          className="w-px h-12 bg-zinc-500 origin-top"
          animate={{ scaleY: [1, 0.3, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>
    </section>
  );
}

